import { useRef } from "react";

// ─── Type Constraints Map ─────────────────────────────────────────────────────
const TYPE_CONSTRAINTS = {
  nin:   { maxLength: 11, pattern: "\\d{11}", inputMode: "numeric", title: "Must be exactly 11 digits" },
  bvn:   { maxLength: 11, pattern: "\\d{11}", inputMode: "numeric", title: "Must be exactly 11 digits" },
  phone: { maxLength: 14, pattern: "\\d{10,14}", inputMode: "tel",     title: "Must be a valid phone number" },
  tin:   { maxLength: 12, pattern: "[A-Z0-9]{8,12}", inputMode: "text",    title: "Valid TIN format required" },
};

const BASE_INPUT =
  "cht-w-full cht-px-4 cht-py-3 cht-border cht-border-gray-200 cht-rounded-xl cht-text-sm " +
  "cht-outline-none focus:cht-border-[#3e57da] focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 " +
  "cht-transition-all cht-bg-white placeholder:cht-text-gray-400";

const FILE_INPUT =
  "cht-w-full cht-text-sm cht-text-gray-600 cht-file:cht-mr-4 cht-file:cht-py-2 cht-file:cht-px-4 " +
  "cht-file:cht-rounded-xl cht-file:cht-border-0 cht-file:cht-text-sm cht-file:cht-font-semibold " +
  "cht-file:cht-bg-[#f2f4fd] cht-file:cht-text-[#3e57da] hover:cht-file:cht-bg-[#e6eaf9] " +
  "cht-border cht-border-gray-200 cht-rounded-xl cht-p-2 cht-outline-none cht-transition-all cht-bg-white";

// ─── Single Field Renderer ─────────────────────────────────────────────────────
const FieldRenderer = ({ field, value, onChange }) => {
  const { name, label, type = "text", placeholder = "", required = true } = field;
  const constraints = TYPE_CONSTRAINTS[type] || {};
  const fileRef = useRef(null);

  if (type === "file") {
    return (
      <div>
        <label className="cht-block cht-text-sm cht-font-semibold cht-text-gray-700 cht-mb-1.5">
          {label} {required && <span className="cht-text-red-500">*</span>}
        </label>
        <input
          id={name}
          ref={fileRef}
          type="file"
          accept="image/*"
          required={required}
          className={FILE_INPUT}
          onChange={(e) => onChange(name, e.target.files[0])}
        />
        <p className="cht-text-xs cht-text-gray-400 cht-mt-1">JPG, PNG or WebP • Max 5 MB</p>
      </div>
    );
  }

  if (type === "textarea") {
    return (
      <div>
        <label className="cht-block cht-text-sm cht-font-semibold cht-text-gray-700 cht-mb-1.5">
          {label} {required && <span className="cht-text-red-500">*</span>}
        </label>
        <textarea
          id={name}
          rows={3}
          value={value || ""}
          required={required}
          placeholder={placeholder}
          className={`${BASE_INPUT} cht-resize-none`}
          onChange={(e) => onChange(name, e.target.value)}
        />
      </div>
    );
  }

  if (type === "select") {
    const options = field.options || [];
    return (
      <div>
        <label className="cht-block cht-text-sm cht-font-semibold cht-text-gray-700 cht-mb-1.5">
          {label} {required && <span className="cht-text-red-500">*</span>}
        </label>
        <select
          id={name}
          value={value || ""}
          required={required}
          className={BASE_INPUT}
          onChange={(e) => onChange(name, e.target.value)}
        >
          <option value="">{placeholder || `Select ${label}`}</option>
          {options.map((opt, idx) => {
            const optVal = typeof opt === "object" ? opt.value : opt;
            const optLbl = typeof opt === "object" ? opt.label : opt;
            return (
              <option key={idx} value={optVal}>
                {optLbl}
              </option>
            );
          })}
        </select>
      </div>
    );
  }

  // default: text / bvn / nin / phone / tin / email / date / number
  const htmlType =
    type === "date" ? "date"
    : type === "email" ? "email"
    : type === "number" ? "number"
    : "text";

  return (
    <div>
      <label className="cht-block cht-text-sm cht-font-semibold cht-text-gray-700 cht-mb-1.5">
        {label} {required && <span className="cht-text-red-500">*</span>}
      </label>
      <input
        id={name}
        type={htmlType}
        value={value || ""}
        required={required}
        placeholder={placeholder || `Enter ${label}`}
        className={BASE_INPUT}
        onChange={(e) => onChange(name, e.target.value)}
        {...(constraints.maxLength   ? { maxLength: constraints.maxLength }   : {})}
        {...(constraints.pattern     ? { pattern: constraints.pattern }         : {})}
        {...(constraints.inputMode   ? { inputMode: constraints.inputMode }     : {})}
        {...(constraints.title       ? { title: constraints.title }             : {})}
      />
      {(type === "nin" || type === "bvn") && (
        <p className="cht-text-xs cht-text-gray-400 cht-mt-1">Exactly 11 digits required</p>
      )}
    </div>
  );
};

// ─── ServiceForm ──────────────────────────────────────────────────────────────
// Props:
//   schema    – array of field descriptor objects from the API
//   values    – { [name]: value } controlled state
//   onChange  – (name, value) => void
const ServiceForm = ({ schema = [], values = {}, onChange }) => {
  if (!schema.length) {
    return (
      <p className="cht-text-sm cht-text-gray-500 cht-italic cht-py-2">
        No input fields defined for this service. Proceed to execute.
      </p>
    );
  }

  return (
    <div className="cht-flex cht-flex-col cht-gap-4">
      {schema.map((field) => (
        <FieldRenderer
          key={field.name}
          field={field}
          value={values[field.name]}
          onChange={onChange}
        />
      ))}
    </div>
  );
};

export default ServiceForm;
